import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ChapterImage } from "../api";
import { useT } from "../i18n";
import { useSession } from "../session";
import { CloseIcon, FlagIcon } from "./icons";
import ReportDialog from "./ReportDialog";

/**
 * Where a reader's chapter pictures are (requirement 0122): set by the reader's
 * text and the player, never by the studio, so only a reader's picture opens
 * full screen and offers a report.
 */
export const PicturePlace = createContext<{
  bookID: string;
  chapterID: string;
  edition?: number;
} | null>(null);

export function usePicturePlace() {
  return useContext(PicturePlace);
}

/**
 * PictureViewer shows one chapter picture full screen. A tap, Escape, the
 * browser's Back or a swipe down closes it. A signed-in reader gets one small
 * report control in the corner, which opens the report dialog.
 */
export default function PictureViewer({
  image,
  onClose,
}: {
  image: ChapterImage;
  onClose: () => void;
}) {
  const t = useT();
  const place = usePicturePlace();
  const { isSignedIn } = useSession();
  const [reporting, setReporting] = useState(false);
  const start = useRef<number | null>(null);
  const closed = useRef(onClose);
  closed.current = onClose;

  // Back closes the picture, not the chapter: an entry is pushed on open, and
  // every way of closing goes back through it, so Back and the × agree. Pushed
  // only if not already there, so a remount (React's development double
  // mount) does not push a second one.
  useEffect(() => {
    if ((history.state as { picture?: string } | null)?.picture !== image.id) {
      history.pushState({ picture: image.id }, "");
    }
    const onPop = () => closed.current();
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, [image.id]);
  const close = () => {
    if ((history.state as { picture?: string } | null)?.picture === image.id) history.back();
    else closed.current();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !reporting) close();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.alt || t.picture.open}
      data-testid="picture-viewer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black"
      onClick={close}
      onPointerDown={(e) => (start.current = e.clientY)}
      onPointerUp={(e) => {
        const from = start.current;
        start.current = null;
        if (from !== null && e.clientY - from > 80) close();
      }}
    >
      <img
        src={image.url}
        alt={image.alt ?? ""}
        width={image.width}
        height={image.height}
        data-testid="picture-viewer-image"
        className="h-full w-full object-contain"
      />
      <div className="absolute inset-x-0 top-0 flex justify-between p-[max(0.5rem,env(safe-area-inset-top))]">
        <button
          type="button"
          aria-label={t.picture.close}
          data-testid="picture-viewer-close"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
          className="grid size-11 place-items-center rounded-full bg-black/40 text-white"
        >
          <CloseIcon />
        </button>
        {place && isSignedIn && (
          <button
            type="button"
            aria-label={t.report.picture}
            title={t.report.picture}
            data-testid="picture-report"
            onClick={(e) => {
              e.stopPropagation();
              setReporting(true);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            className="grid size-11 place-items-center rounded-full bg-black/40 text-white/80"
          >
            <FlagIcon className="size-4" />
          </button>
        )}
      </div>
      {reporting && place && (
        <div
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          <ReportDialog
            bookID={place.bookID}
            edition={place.edition}
            place={{ place: "picture", chapter_id: place.chapterID, picture_id: image.id }}
            about={image.caption || image.alt || t.report.picture}
            onClose={() => setReporting(false)}
          />
        </div>
      )}
    </div>
  );
}
